import {
  validateName,
  validateSocialSecurityNumber,
  validateAlienRegistrationNumber,
} from '../../../../utils/validations';
import { hasAnyMinorIdentificationFieldValue } from '../../utils/validators';
import { generateRaceField } from '../common';

function buildMinorsSection({
  isProspect,
  isAffordable,
  affordablePrograms = [],
  propertyStateCode,
}) {
  return {
    name: 'minorsSection',
    title: 'MINORS (UNDER 18)',
    titleVariant: 'subsection',
    divider: true,
    container: 'default',
    fields: [
      {
        name: 'areThereAnyMinors',
        variant: 'radioGroup',
        label: 'Are there any minors under 18 years of age?',
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
        rules: {
          required: true,
        },
        changeActions: [
          {
            type: 'updateField',
            target: 'minors',
            when: {
              eq: 'no',
            },
            value: [],
          },
          {
            type: 'updateField',
            target: 'minors',
            value: [
              {
                minorLegalFirstName: '',
                minorMiddleName: '',
                minorLastName: '',
                minorPreferredName: '',
                minorSuffix: '',
                minorDateOfBirth: null,
                minorSocialSecurityNumber: '',
                minorAlienRegistrationNumber: '',
                minorGender: '',
                minorAffordableRelationship: '',
                minorEthnicity: '',
                minorRace: '',
              },
            ],
            when: {
              eq: 'yes',
            },
          },
        ],
        sx: { flexDirection: 'row' },
        GridProps: { xs: 12 },
      },
      ...(isProspect
        ? [
            buildMinorsGroup({
              isAffordable,
              affordablePrograms,
              propertyStateCode,
            }),
          ]
        : []),
    ],
  };
}

function buildMinorsGroup({
  isAffordable,
  affordablePrograms = [],
  propertyStateCode,
}) {
  return {
    name: 'minors',
    title: 'MINOR',
    container: 'collapse',
    GridProps: { xs: 12, marginTop: 1.5 },
    groupProps: {
      addButtonText: 'Add Additional Minor',
      max: 9,
      min: 1,
      watchFields: ['areThereAnyMinors'],
      hidden: ({ getValues }) => getValues('areThereAnyMinors') !== 'yes',
      GridProps: { width: '100%' },
    },
    fields: [
      {
        name: 'minorLegalFirstName',
        variant: 'text',
        label: 'Legal First Name',
        rules: {
          required: true,
          maxLength: 35,
          validate: (value) => {
            if (!validateName(value)) {
              return 'Invalid name';
            }
            return true;
          },
        },
        GridProps: { xs: 12, md: 3 },
      },
      {
        name: 'minorMiddleName',
        variant: 'text',
        label: 'Middle Name',
        rules: {
          maxLength: 35,
          validate: (value) => {
            if (value && !validateName(value)) {
              return 'Invalid name';
            }
            return true;
          },
        },
        GridProps: { xs: 12, md: 3 },
      },
      {
        name: 'minorLastName',
        variant: 'text',
        label: 'Last Name',
        rules: {
          required: true,
          maxLength: 35,
          validate: (value) => {
            if (!validateName(value)) {
              return 'Invalid name';
            }
            return true;
          },
        },
        GridProps: { xs: 12, md: 3 },
      },
      {
        name: 'minorSuffix',
        variant: 'singleSelect',
        label: 'Suffix',
        placeholder: 'Select',
        GridProps: { xs: 12, md: 3 },
      },
      {
        name: 'minorPreferredName',
        variant: 'text',
        label: 'Preferred Name',
        rules: {
          maxLength: 35,
          validate: (value) => {
            if (value && !validateName(value)) {
              return 'Invalid name';
            }
            return true;
          },
        },
        GridProps: { xs: 12, md: 3 },
      },
      {
        name: 'minorDateOfBirth',
        variant: 'date',
        label: 'Date of Birth',
        placeholder: 'MM/DD/YYYY',
        rules: {
          required: true,
          validate: (value) => {
            if (!value) return true;

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const eighteenYearsAgo = new Date();
            eighteenYearsAgo.setFullYear(today.getFullYear() - 18);
            eighteenYearsAgo.setHours(0, 0, 0, 0);

            // Affordable programs count an expected child as a household
            // member, dated with the anticipated due date. The resident portal
            // takes a due date up to a year ahead, so the same window applies
            // here, or an application the portal accepted could not be saved.
            const latestBirthDate = new Date(today);
            if (isAffordable) {
              latestBirthDate.setFullYear(today.getFullYear() + 1);
            }

            const birthDate = new Date(value);
            birthDate.setHours(0, 0, 0, 0);

            // A year typed with two digits ("14") arrives as the year 14; it
            // would also fail the under-18 rule below, but that message sends
            // the user looking at the wrong thing.
            // Local midnight, like birthDate above: the ISO string form would
            // parse as UTC and shift the boundary by the timezone offset.
            if (birthDate < new Date(1900, 0, 1)) {
              return 'Invalid year. Enter four digits (e.g. 2014).';
            }
            if (birthDate > latestBirthDate) {
              return isAffordable
                ? 'Date of Birth cannot be more than a year in the future'
                : 'Date of Birth cannot be a future date';
            }
            if (birthDate <= eighteenYearsAgo) {
              return 'Minor must be under 18 years of age';
            }
            return true;
          },
        },
        GridProps: { xs: 12, md: 3 },
      },
      ...(isAffordable
        ? buildAffordableFields({ affordablePrograms, propertyStateCode })
        : []),
    ],
  };
}

function buildAffordableFields({ affordablePrograms = [], propertyStateCode }) {
  const hasHUD = affordablePrograms.includes('HUD');
  const hasRD = affordablePrograms.includes('RD');

  return [
    {
      name: 'minorSocialSecurityNumber',
      variant: 'text',
      label: 'Social Security Number or TIN',
      rules: {
        // A minor needs one identification number, not both, so this field
        // carries the requirement for the pair and an alien registration number
        // satisfies it just as well. Same rule the affordable occupants section
        // of the previous application form applies, and the same wording.
        validate: (value, getValues) => {
          if (value) {
            if (!validateSocialSecurityNumber(value)) {
              return 'Invalid Social Security Number';
            }
          }
          return hasAnyMinorIdentificationFieldValue(getValues)
            ? true
            : 'SSN/TIN or Alien Registration # is required';
        },
      },
      watchFields: [
        'minorSocialSecurityNumber',
        'minorAlienRegistrationNumber',
      ],
      GridProps: { xs: 12, md: 3 },
    },
    {
      name: 'minorAlienRegistrationNumber',
      variant: 'text',
      label: 'Alien Registration Number',
      rules: {
        validate: (value) => {
          if (value) {
            if (!validateAlienRegistrationNumber(value)) {
              return 'Invalid value';
            }
          }
          return true;
        },
      },
      GridProps: { xs: 12, md: 3 },
    },
    {
      name: 'minorGender',
      variant: 'singleSelect',
      label: 'Gender',
      placeholder: 'Select',
      GridProps: { xs: 12, md: 3 },
      rules: {
        required: true,
      },
      options: [
        { value: 'male', text: 'Male' },
        { value: 'female', text: 'Female' },
        {
          value: 'doNotWishToProvide',
          text: 'Do not wish to provide',
        },
      ],
    },
    {
      name: 'minorAffordableRelationship',
      variant: 'singleSelect',
      label: 'Affordable Relationship',
      placeholder: 'Select',
      GridProps: { xs: 12, md: 3 },
      rules: {
        required: true,
      },
    },
    {
      name: 'minorEthnicity',
      variant: 'singleSelect',
      label: 'Ethnicity',
      placeholder: 'Select',
      GridProps: { xs: 12, md: 3 },
      rules: {
        required: true,
      },
      options: [
        { value: 'hispanic', text: 'Hispanic/Latino' },
        { value: 'notHispanic', text: 'Not Hispanic/Latino' },
        {
          value: 'doNotWishToProvide',
          text: 'Do not wish to provide',
        },
      ],
    },
    generateRaceField({
      propertyStateCode,
      name: 'minorMultiRace',
    }),
    ...(hasRD
      ? [
          {
            name: 'minorRaceSource',
            variant: 'radioGroup',
            label: 'Race Source',
            options: [
              {
                value: 'C-Customer Provided',
                label: 'Customer Provided',
              },
              {
                value: 'E-Employee Observed',
                label: 'Employee Observed',
              },
            ],
            rules: {
              required: true,
            },
            sx: { flexDirection: 'row' },
            GridProps: { xs: 12 },
          },
        ]
      : []),
    {
      name: 'isMinorAStudent',
      variant: 'radioGroup',
      label: 'Is the minor a student?',
      options: [
        { label: 'Full-Time K-12', value: 'f_tK_12' },
        { label: 'Full-Time Post 12', value: 'f_tPost12' },
        { label: 'Part-Time', value: 'p_t' },
        { label: 'Not a Student', value: 'n_s' },
      ],
      rules: {
        required: true,
      },
      sx: { flexDirection: 'row' },
      GridProps: { xs: 12, md: 6 },
    },
    {
      name: 'possessJointCustodyOfMinor',
      variant: 'radioGroup',
      label: 'Do you possess joint custody of the minor?',
      // Must offer every value ApplicantMinor.jointCustody can hold. This form
      // round-trips the stored value through possessJointCustodyOfMinor
      // (utils/formMappers), so an option missing here renders the group blank
      // and overwrites the stored answer on save. The other two screens that
      // write this field are components/LeaseAffordableApplicantFormCommonSections/configuration
      // and containers/GeneralAffordable/MinorCustodyModalForm.
      options: [
        {
          label: 'Yes, More than 50%',
          value: 'yes50',
        },
        {
          label: 'Yes, More than 50% - Dependent & Child Care Allowance apply',
          value: 'yes50Allowance',
        },
        {
          label: 'Yes, Less than 50%',
          value: 'yesLessThan50',
        },
        {
          label: 'No joint custody',
          value: 'no',
        },
      ],
      rules: {
        required: true,
      },
      sx: { flexDirection: 'row' },
      GridProps: { xs: 12, md: 6 },
    },
    ...(hasHUD
      ? []
      : [
          { variant: 'custom', component: <div style={{ height: 5 }} /> },
          buildMinorDisabilitiesSection(),
        ]),
  ];
}

function buildMinorDisabilitiesSection() {
  return {
    name: 'disabilities',
    container: 'default',
    title: 'DISABILITIES',
    titleVariant: 'collapse',
    fields: [
      {
        name: 'isMinorDisabled',
        variant: 'radioGroup',
        label: 'Is the minor disabled?',
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
          {
            label: 'Do not wish to provide',
            value: 'doNotWishToProvide',
          },
        ],
        changeActions: [
          {
            type: 'updateField',
            target: 'requiredReasonableAccommodation',
            value: undefined,
            when: {
              eq: 'no',
            },
          },
          {
            type: 'updateField',
            target: 'requiredReasonableAccommodation',
            value: undefined,
            when: {
              eq: 'doNotWishToProvide',
            },
          },
        ],
        rules: {
          required: true,
        },
        sx: { flexDirection: 'row' },
        GridProps: { xs: 12, md: 5 },
      },
      {
        name: 'requiredReasonableAccommodation',
        variant: 'radioGroup',
        label: 'Does the minor require a reasonable accommodation?',
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
          {
            label: 'Do not wish to provide',
            value: 'doNotWishToProvide',
          },
        ],
        rules: {
          required: true,
        },
        hidden: ({ getValues }) => getValues('isMinorDisabled') !== 'yes',
        watchFields: ['isMinorDisabled'],
        sx: { flexDirection: 'row' },
        GridProps: { xs: 12, md: 6 },
      },
    ],
  };
}

export default buildMinorsSection;
